import {
  getSessionData,
  removeSessionData,
  setSessionData,
} from "@helpers/accessLocalStorage/accessLocalStorage";
import type {
  DirectLabCheckoutAnalyticsContext,
  DirectLabCheckoutRouteContext,
} from "@shared/hooks/useAnalyticsLogger/DirectLabCheckoutAnalyticsTypes";
import { isDirectLabCheckoutItemType } from "@shared/hooks/useAnalyticsLogger/DirectLabCheckoutAnalyticsTypes";

const STORAGE_KEY = "directLabCheckoutAnalyticsContext";
const MAX_AGE_MS = 60 * 60 * 1000;

type StoredDirectLabCheckoutAnalyticsContext = DirectLabCheckoutAnalyticsContext & {
  created_at: number;
};

type StoredContexts = Record<string, StoredDirectLabCheckoutAnalyticsContext>;

const isStoredDirectLabCheckoutAnalyticsContext = (
  value: unknown
): value is StoredDirectLabCheckoutAnalyticsContext => {
  if (!value || typeof value !== "object") return false;

  const context = value as Partial<StoredDirectLabCheckoutAnalyticsContext>;

  return (
    typeof context.checkout_source === "string" &&
    typeof context.created_at === "number" &&
    typeof context.item_id === "string" &&
    isDirectLabCheckoutItemType(context.item_type) &&
    typeof context.session_id === "string" &&
    typeof context.store_id === "string" &&
    typeof context.store_slug === "string"
  );
};

const getStorageKey = (route: DirectLabCheckoutRouteContext) =>
  JSON.stringify([route.store_slug, route.item_type, route.item_id]);

const getStoredContexts = (): StoredContexts => {
  const value: unknown = getSessionData(STORAGE_KEY);

  if (!value || typeof value !== "object") return {};

  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, StoredDirectLabCheckoutAnalyticsContext] =>
        isStoredDirectLabCheckoutAnalyticsContext(entry[1])
    )
  );
};

const setDirectLabCheckoutAnalyticsContext = (context: DirectLabCheckoutAnalyticsContext) => {
  const storedContexts = getStoredContexts();

  setSessionData(STORAGE_KEY, {
    ...storedContexts,
    [getStorageKey(context)]: {
      ...context,
      created_at: Date.now(),
    },
  });
};

const getDirectLabCheckoutAnalyticsContext = (
  route: DirectLabCheckoutRouteContext
): DirectLabCheckoutAnalyticsContext | null => {
  const storedContexts = getStoredContexts();
  const storageKey = getStorageKey(route);
  const context = storedContexts[storageKey];

  if (!context) return null;

  if (Date.now() - context.created_at > MAX_AGE_MS) {
    delete storedContexts[storageKey];

    if (Object.keys(storedContexts).length === 0) {
      removeSessionData(STORAGE_KEY);
    } else {
      setSessionData(STORAGE_KEY, storedContexts);
    }

    return null;
  }

  return {
    checkout_source: context.checkout_source,
    item_id: context.item_id,
    item_type: context.item_type,
    session_id: context.session_id,
    store_id: context.store_id,
    store_slug: context.store_slug,
  };
};

const removeDirectLabCheckoutAnalyticsContext = (route?: DirectLabCheckoutRouteContext) => {
  if (!route) {
    removeSessionData(STORAGE_KEY);

    return;
  }

  const storedContexts = getStoredContexts();
  delete storedContexts[getStorageKey(route)];

  if (Object.keys(storedContexts).length === 0) {
    removeSessionData(STORAGE_KEY);
  } else {
    setSessionData(STORAGE_KEY, storedContexts);
  }
};

export {
  getDirectLabCheckoutAnalyticsContext,
  removeDirectLabCheckoutAnalyticsContext,
  setDirectLabCheckoutAnalyticsContext,
};
